import { useInjection } from 'inversify-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { API } from '../services/HTTP';
import { Button, Form, Spinner, Table } from 'react-bootstrap';
import { IInfluxAPI } from '../services/influx/InfluxAPIInterface';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { InfluxAPIMock } from '../services/influx/InfluxAPIMock';
import { InfluxAPI } from '../services/influx/InfluxAPI';
import { TimeSeriesChartRow, toTimeSeriesChartRows } from '../components/TileDisplay';

ChartJS.register(...registerables);

function buildChartDatasets(rows: TimeSeriesChartRow[]) {
    if (!rows.length) {
        return [];
    }

    const valueKeys = Array.from(
        new Set(rows.flatMap((row) => Object.keys(row).filter((key) => key !== 'timestamp')))
    );

    return valueKeys
        .map((key) => ({
            label: key,
            data: rows
                .filter((row) => typeof row[key] === 'number')
                .map((row) => ({ x: row.timestamp, y: row[key] as number })),
            fill: false,
            tension: 0.1,
        }))
        .filter((dataset) => dataset.data.length > 0);
}

function buildTableRows(rows: TimeSeriesChartRow[]) {
    return [...rows].sort((left, right) => new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime());
}

export function InfluxPage() {
    const httpClient = useInjection<API>('API');
    const influxAPIActual = useMemo(() => new InfluxAPI(httpClient), [httpClient]);
    const influxAPIMock = useMemo(() => new InfluxAPIMock(), []);

    const [influxAPI, setInfluxAPI] = useState<IInfluxAPI>(influxAPIActual);
    const [sql, setSQL] = useState('SELECT "time", "field_0", field_1 FROM "random_data" WHERE "tag_0"=\'0\' and time >= now() - interval \'150 minute\' order by time');
    const [data, setData] = useState<any[]>([]);
    const [tableRows, setTableRows] = useState<TimeSeriesChartRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [mockMode, setMockMode] = useState(localStorage.getItem("influx_mode") === "mock");
    const [showChart, setShowChart] = useState(false);

    const switchMode = useCallback(async () => {
        setMockMode(!mockMode);
        localStorage.setItem("influx_mode", !mockMode ? "mock" : "real");
    }, [mockMode]);

    useEffect(() => {
        if (mockMode) {
            setInfluxAPI(influxAPIMock);
        } else {
            setInfluxAPI(influxAPIActual);
        }
    }, [mockMode, influxAPIMock, influxAPIActual]);

    const runSQL = useCallback(async () => {
        setLoading(true);
        setError(null);
        setData([]);
        setTableRows([]);

        try {
            const result = await influxAPI.postInfluxSQL({ sql });
            const normalizedRows = toTimeSeriesChartRows(result.rows);

            setData(buildChartDatasets(normalizedRows));
            setTableRows(buildTableRows(normalizedRows));
        } catch (e: any) {
            setError(e.message);
            setTableRows([]);
        } finally {
            setLoading(false);
        }
    }, [influxAPI, sql]);

    const chartData = { datasets: data };

    const chartOptions = {
        responsive: true,
        scales: {
            x: {
                type: 'time',
                time: { unit: 'minute' },
                title: { display: true, text: 'time' },
            },
            y: {
                title: { display: true, text: 'value' },
            },
        },
    };

    return (
        <div className="p-3">
            <h3>InfluxDB v3 SQL Query</h3>

            <Button variant="secondary" className="mb-2 me-2" onClick={switchMode}>
                Mode : {mockMode ? "Mock" : "Server-configured Influx"}
            </Button>

            {!mockMode && (
                <p className="text-muted">
                    Connection details and credentials are resolved on the server. Only SQL is sent from the browser.
                </p>
            )}

            <Form className="mb-3">
                <Form.Group>
                    <Form.Label>SQL</Form.Label>
                    <Form.Control as="textarea" rows={4} value={sql} onChange={e => setSQL(e.target.value)} />
                </Form.Group>
                <Button onClick={runSQL} disabled={loading}>
                    {loading ? <Spinner animation="border" size="sm" /> : "Execute"}
                </Button>
            </Form>

            {error && <div className="text-danger">Error: {error}</div>}

            {!error && data.length > 0 && (
                <Button variant="outline-primary" className="mb-2" onClick={() => setShowChart(!showChart)}>
                    Display : {showChart ? "Table" : "Graph"}
                </Button>
            )}

            {data.length > 0 && showChart && (
                <Chart type="line" data={chartData} options={chartOptions as any} />
            )}

            {tableRows.length > 0 && !showChart && (
            <Table striped bordered size="sm">
                <thead>
                <tr>{Object.keys(tableRows[0]).map(k => <th key={k}>{k}</th>)}</tr>
                </thead>
                <tbody>
                {tableRows.map((row, i) => (
                    <tr key={i}>
                    {Object.values(row).map((v, j) => (
                        <td key={j}>{String(v)}</td>
                    ))}
                    </tr>
                ))}
                </tbody>
            </Table>
            )}
        </div>
    );
}
